import React, { useState, useEffect } from 'react';
import axios from "axios";
import {useParams} from "react-router-dom";
import {PercentageInput, DollarInput} from "./InputFormFields";


function StatementSummaryForm({
  revolver,
  triggerLabel = 'Add Statement Summary',
  triggerClassName = 'btn btn-secondary',
  onSuccess,
  initialData = null,
  recordId = null,
}) {
  const { id } = useParams();
  const [isModalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [formData, setFormData] = useState({
    billing_cycle_start_date: '',
    billing_cycle_end_date: '',
    payment_due_date: '',
    payment_due: '',
    purchases_apr: '',
    previous_balance: '',
    new_balance: '',
    balance_subject_to_interest_rate: '',
    available_credit: '',
    payments: '',
    transactions: '',
    other_credits: '',
    cash_advance_credit_limit: '',
    available_credit_for_cash_advances: '',
    cash_advances: '',
    interest_charged: '',
    interest_charged_ytd: '',
    fees_charged: '',
    fees_charged_ytd: '',
    credit_limit: '',
    // Rewards-related (optional)
    rewards_balance: '',
    rewards_previous_balance: '',
    rewards_earned_this_period: '',
    rewards_redeemed_this_period: '',
  });

  const openModal = () => {
    // If editing, prefill form data with provided initialData
    if (initialData) {
      const toNum = (v) => {
        if (v === null || v === undefined || v === '') return '';
        const n = parseFloat(v);
        return isNaN(n) ? '' : n;
      };

      const editData = {
        billing_cycle_start_date: initialData.billing_cycle_start_date || '',
        billing_cycle_end_date: initialData.billing_cycle_end_date || '',
        payment_due_date: initialData.payment_due_date || '',
        payment_due: toNum(initialData.payment_due),
        purchases_apr: initialData.purchases_apr !== undefined && initialData.purchases_apr !== null
          ? toNum(initialData.purchases_apr)
          : '',
        previous_balance: toNum(initialData.previous_balance),
        new_balance: toNum(initialData.new_balance),
        balance_subject_to_interest_rate: toNum(initialData.balance_subject_to_interest_rate),
        available_credit: toNum(initialData.available_credit),
        payments: initialData.payments ? Math.abs(toNum(initialData.payments)) : '',
        transactions: toNum(initialData.transactions),
        other_credits: initialData.other_credits ? Math.abs(toNum(initialData.other_credits)) : '',
        cash_advance_credit_limit: toNum(initialData.cash_advance_credit_limit),
        available_credit_for_cash_advances: toNum(initialData.available_credit_for_cash_advances),
        cash_advances: toNum(initialData.cash_advances),
        interest_charged: toNum(initialData.interest_charged),
        interest_charged_ytd: toNum(initialData.total_interest_charged_ytd ?? initialData.interest_charged_ytd),
        fees_charged: toNum(initialData.fees_charged),
        fees_charged_ytd: toNum(initialData.total_fees_charged_ytd ?? initialData.fees_charged_ytd),
        credit_limit: toNum(initialData.credit_limit),
        rewards_balance: toNum(initialData.rewards_balance),
        rewards_previous_balance: toNum(initialData.rewards_previous_balance),
        rewards_earned_this_period: toNum(initialData.rewards_earned_this_period),
        rewards_redeemed_this_period: initialData.rewards_redeemed_this_period ? Math.abs(toNum(initialData.rewards_redeemed_this_period)) : '',
      };
      // For HELOCs, the payment due is effectively the interest accrued for the period
      if (revolver && revolver.revolver_type === 'heloc') {
        editData.payment_due = editData.interest_charged;
      }
      setFormData(prev => ({ ...prev, ...editData }));
    }
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  // Pre-populate form with heloc data when component mounts or heloc changes
  useEffect(() => {
    if (revolver) {
      const newFormData = {
        credit_limit: revolver.current_credit_limit || '',
        purchases_apr: revolver.current_rate || 0.00,
        cash_advances: 0,
        // set other defaults here
      };

      // Only set 'available_credit_for_cash_advances' if revolver_type !== 'heloc'
      if (revolver.revolver_type !== 'heloc') {
        newFormData['cash_advance_credit_limit'] = revolver.current_cash_advance_credit_limit || '';
      }

      setFormData(prev => ({ ...prev, ...newFormData }));
    }
  }, [revolver]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    // Process form data here
    // Ensure payments and other_credits and rewards_redeemed are negative
    const payments = formData['payments'] ? -Math.abs(parseFloat(formData['payments'])) : 0;
    const other_credits = formData['other_credits'] ? -Math.abs(parseFloat(formData['other_credits'])) : 0;
    const rewards_redeemed = formData['rewards_redeemed_this_period'] ? -Math.abs(parseFloat(formData['rewards_redeemed_this_period'])) : null;

    const newStatementSummaryData = {
      'revolver': id,
      'billing_cycle_start_date': formData['billing_cycle_start_date'],
      'billing_cycle_end_date': formData['billing_cycle_end_date'],
      'billing_cycle_n_days': null,
      'payment_due_date': formData['payment_due_date'],
      'previous_balance': formData['previous_balance'],
      'payments': payments,
      'other_credits': other_credits,
      'transactions': formData['transactions'],
      'cash_advances': formData['cash_advances'],
      'fees_charged': formData['fees_charged'],
      'interest_charged': formData['interest_charged'],
      'new_balance': formData['new_balance'],
      'credit_limit': formData['credit_limit'],
      'available_credit': formData['available_credit'],
      'cash_advance_credit_limit': formData['cash_advance_credit_limit'] || null,
      'available_credit_for_cash_advances': formData['available_credit_for_cash_advances'] || null,
      'rewards_balance': formData['rewards_balance'] || null,
      'rewards_previous_balance': formData['rewards_previous_balance'] || null,
      'rewards_earned_this_period': formData['rewards_earned_this_period'] || null,
      'rewards_redeemed_this_period': rewards_redeemed,
      'total_fees_charged_ytd': formData['fees_charged_ytd'] || 0,
      'total_interest_charged_ytd': formData['interest_charged_ytd'],
      'purchases_apr': formData['purchases_apr'],
      'purchase_apr_code': formData['purchase_apr_code'] || null,
      'purchase_apr_calculation': formData['purchase_apr_calculation'] || null,
      'balance_subject_to_interest_rate': formData['balance_subject_to_interest_rate'],
      };

      try {
          let resp;
          if (recordId) {
            // Update existing summary
            resp = await axios.put(
              `/api/balance_sheet/financing/revolvers/statement-summary-data/${recordId}/`,
              newStatementSummaryData
            );
          } else {
            // Create new summary (DRF expects JSON by default)
            resp = await axios.post(
              `/api/balance_sheet/financing/revolvers/${id}/statement-summary-data/`,
              newStatementSummaryData
            );
          }
          // Success: show a short confirmation, trigger parent refresh, and close modal
          setSuccessMsg('Statement summary saved.');
          if (typeof onSuccess === 'function') {
            try { await onSuccess(resp?.data); } catch(e) { /* noop */ }
          }
          setModalOpen(false);
      } catch (error) {
          console.error('Error saving statement summary:', error);
          // Try to surface server-side validation errors if present
          const msg = error?.response?.data ? JSON.stringify(error.response.data) : 'There was an error saving the statement summary. Please check your input.';
          setError(msg);
      } finally {
          setSubmitting(false);
      }

  };

  const inputClass = "form-control bg-dark text-light border-secondary";
  const labelStyle = { color: '#e5e7eb', fontWeight: 500 };
  const helpTextStyle = { color: '#9ca3af', fontSize: '0.85rem' };

  return (
    <div>
      {/* Main application content */}
      <button onClick={openModal} className={triggerClassName}>{triggerLabel}</button>

      {/* Modal overlay */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1050
        }}>
          {/* Modal content */}
          <div style={{
            backgroundColor: '#111827', color: '#e5e7eb', padding: '20px', borderRadius: '12px',
            minWidth: '320px', maxWidth: '960px', width: '90%',
            border: '1px solid #374151', boxShadow: '0 10px 25px rgba(0,0,0,0.6)'
          }}>
            <h2 style={{ marginBottom: '12px' }}>{recordId ? 'Edit Statement Summary' : 'New Statement Summary Details'}</h2>
            {error && (
              <div className="alert alert-danger" role="alert">{error}</div>
            )}
            {successMsg && (
              <div className="alert alert-success" role="alert">{successMsg}</div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Statement Period Start
                  <input
                    type="date"
                    name="billing_cycle_start_date"
                    value={formData.billing_cycle_start_date}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={{ width: '100%' }}
                  />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Statement Period End
                  <input
                    type="date"
                    name="billing_cycle_end_date"
                    value={formData.billing_cycle_end_date}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={{ width: '100%' }}
                  />
                </label>
              </div>
              <br />
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Payment Amount Due
                  <DollarInput
                    name="payment_due"
                    value={formData.payment_due}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, payment_due: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Payment Due Date
                  <input
                    type="date"
                    name="payment_due_date"
                    value={formData.payment_due_date}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={{ width: '100%' }}
                  />
                </label>
              </div>
              <br />
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Starting Balance
                  <DollarInput
                    name="previous_balance"
                    value={formData.previous_balance}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, previous_balance: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Ending Balance
                  <DollarInput
                    name="new_balance"
                    value={formData.new_balance}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, new_balance: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Average Daily Balance
                  <DollarInput
                    name="balance_subject_to_interest_rate"
                    value={formData.balance_subject_to_interest_rate}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, balance_subject_to_interest_rate: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
              </div>
              <br />
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Total Payments
                  <DollarInput
                    name="payments"
                    value={formData.payments}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, payments: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Total Credits
                  <DollarInput
                    name="other_credits"
                    value={formData.other_credits}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, other_credits: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Total Transactions
                  <DollarInput
                    name="transactions"
                    value={formData.transactions}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, transactions: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Total Cash Advances
                  <DollarInput
                    name="cash_advances"
                    value={formData.cash_advances}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, cash_advances: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
              </div>
              <br/>
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Available Credit
                  <DollarInput
                    name="available_credit"
                    value={formData.available_credit}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, available_credit: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Credit Limit
                  <DollarInput
                    name="credit_limit"
                    value={formData.credit_limit}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, credit_limit: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
              </div>
              <br/>
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* Show these fields only if revolver.revolver_type !== 'heloc' */}
                {revolver.revolver_type !== 'heloc' && (
                  <>
                    <label style={{ flex: 1, ...labelStyle }}>
                      Available Cash Advance Credit
                      <DollarInput
                        name="available_credit_for_cash_advances"
                        value={formData.available_credit_for_cash_advances}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, available_credit_for_cash_advances: val }));
                        }}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ flex: 1, ...labelStyle }}>
                      Cash Advance Credit Limit
                      <DollarInput
                        name="cash_advance_credit_limit"
                        value={formData.cash_advance_credit_limit}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, cash_advance_credit_limit: val }));
                        }}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Interest Rate (APR)
                  <PercentageInput
                    value={formData.purchases_apr}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, purchases_apr: val }));
                    }}
                    className={inputClass}
                    style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Interest Charged
                    <DollarInput
                      name="interest_charged"
                      value={formData.interest_charged}
                      onChange={(val) => {
                        // Always update interest_charged
                        setFormData(prev => {
                          // For HELOCs, mirror to payment_due if user hasn't overridden it
                          if (revolver && revolver.revolver_type === 'heloc') {
                            const shouldMirror = prev.payment_due === '' || prev.payment_due === prev.interest_charged;
                            return {
                              ...prev,
                              interest_charged: val,
                              payment_due: shouldMirror ? val : prev.payment_due,
                            };
                          }
                          return { ...prev, interest_charged: val };
                        });
                      }}
                      className={inputClass}
                      style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Interest Charged YTD
                    <DollarInput
                      name="interest_charged_ytd"
                      value={formData.interest_charged_ytd}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, interest_charged_ytd: val }));
                      }}
                      className={inputClass}
                      style={{ width: '100%' }}
                    />
                </label>
              </div>
              <br />
              <div style={{ display: 'flex', gap: '10px' }}>
                <label style={{ flex: 1, ...labelStyle }}>
                  Fees Charged
                    <DollarInput
                      name="fees_charged"
                      value={formData.fees_charged}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, fees_charged: val }));
                      }}
                      className={inputClass}
                      style={{ width: '100%' }}
                    />
                </label>
                <label style={{ flex: 1, ...labelStyle }}>
                  Fees Charged YTD
                    <DollarInput
                      name="fees_charged_ytd"
                      value={formData.fees_charged_ytd}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, fees_charged_ytd: val }));
                      }}
                      className={inputClass}
                      style={{ width: '100%' }}
                    />
                </label>
              </div>
              <br />
              {/* Optional Rewards section (collapsed by default) */}
              <div style={{ borderTop: '1px solid #374151', margin: '12px 0', paddingTop: '8px' }}>
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm"
                  onClick={() => setShowRewards(v => !v)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {showRewards ? 'Hide Rewards Fields' : 'Show Rewards Fields (optional)'}
                </button>
                {showRewards && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <label style={{ flex: 1, minWidth: '220px', ...labelStyle }}>
                      Rewards Balance
                      <input
                        type="number"
                        step="0.01"
                        name="rewards_balance"
                        value={formData.rewards_balance}
                        onChange={handleInputChange}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ flex: 1, minWidth: '220px', ...labelStyle }}>
                      Rewards Previous Balance
                      <input
                        type="number"
                        step="0.01"
                        name="rewards_previous_balance"
                        value={formData.rewards_previous_balance}
                        onChange={handleInputChange}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ flex: 1, minWidth: '220px', ...labelStyle }}>
                      Rewards Earned This Period
                      <input
                        type="number"
                        step="0.01"
                        name="rewards_earned_this_period"
                        value={formData.rewards_earned_this_period}
                        onChange={handleInputChange}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ flex: 1, minWidth: '220px', ...labelStyle }}>
                      Rewards Redeemed This Period
                      <input
                        type="number"
                        step="0.01"
                        name="rewards_redeemed_this_period"
                        value={formData.rewards_redeemed_this_period}
                        onChange={handleInputChange}
                        className={inputClass}
                        style={{ width: '100%' }}
                      />
                    </label>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    recordId ? 'Update Summary' : 'Save Summary'
                  )}
                </button>
              </div>
              <div style={{ marginTop: '8px' }}>
                <small style={helpTextStyle}>Fields support positive numbers; dates affect how statements are grouped.</small>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatementSummaryForm;